import { useState } from "react";
import { useAgent } from "@/context/agent-context";
import { usePermissions } from "@/context/permissions-context";
import { useQueryClient } from "@tanstack/react-query";
import { getListAgentsQueryKey } from "@workspace/api-client-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Smartphone, BellRing, Loader2, Settings as SettingsIcon } from "lucide-react";
import { useLang } from "@/context/lang-context";
import { dir, tt } from "@/lib/i18n";

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem("waslacrm_token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

// WhatsApp-deliverable notification categories the agent can opt into. Mirrors the
// shape used by the Settings page (notif_prefs: { <cat>: { web, email, wa } }).
const CATEGORIES = [
  { key: "mention", label: "عند الإشارة لي في محادثة (منشن)" },
  { key: "transfer", label: "عند تحويل محادثة لي" },
  { key: "sla", label: "تنبيهات التأخّر في الرد (SLA)" },
];

/**
 * First-login setup for agents. Managers create the account; the agent then sets
 * their own phone (so alerts can reach them) and which alerts they want — instead
 * of relying on every employee to find the Settings page. Shows once, while the
 * agent has no phone on file; "لاحقاً" defers it to the next login.
 */
export function OnboardingDialog() {
  const { currentAgent } = useAgent();
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState("");
  const [waOn, setWaOn] = useState<Record<string, boolean>>({ mention: true, transfer: true, sla: true });
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // التنبيهاتُ تصل واتساب — فشركةٌ أُطفئت عنها وحدةُ «واتساب» لا يُعرض لها هذا الإعداد.
  // وينتظر وصولَ المميزات كي لا يلمع لحظةً قبل أن تُعرف.
  const { hasFeature, isLoaded } = usePermissions();
  // كان نصّاً عربيّاً ثابتاً — فشركةٌ واجهتُها إنجليزيّة رأته عربيّاً (بلاغ المالك
  // 2026-10-03). الاشتراكُ في اللغة يعيد رسمه حين تتبدّل.
  useLang();

  const open = isLoaded && hasFeature("whatsapp") && !!currentAgent && !currentAgent.phone && !dismissed;
  if (!open || !currentAgent) return null;

  const phoneValid = phone.trim().replace(/\D/g, "").length >= 8;

  async function save() {
    if (!currentAgent || !phoneValid) return;
    setSaving(true);
    try {
      await fetch(`/api/agents/${currentAgent.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const prefs = {
        mention: { web: true, email: true, wa: waOn.mention },
        transfer: { web: true, email: false, wa: waOn.transfer },
        sla: { web: true, email: true, wa: waOn.sla },
      };
      localStorage.setItem("waslacrm_notif_prefs", JSON.stringify(prefs));
      await fetch(`/api/agents/${currentAgent.id}/notif-prefs`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(prefs),
      }).catch(() => {});
      // Refresh the agent so currentAgent.phone is set → this dialog auto-closes.
      await queryClient.invalidateQueries({ queryKey: getListAgentsQueryKey() });
    } catch { /* best-effort */ }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" dir={dir()}>
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="text-center space-y-1">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <BellRing className="h-6 w-6 text-emerald-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-50">{tt("مرحباً")} {currentAgent.name} 👋</h2>
          <p className="text-sm text-slate-500">{tt("إعداد سريع لمرة وحدة عشان توصلك التنبيهات على واتساب")}</p>
        </div>

        <div>
          <label className="text-sm font-medium mb-1.5 flex items-center gap-1.5 text-slate-700 dark:text-slate-200">
            <Smartphone className="h-4 w-4 text-emerald-600" /> {tt("رقم تلفونك (مع رمز الدولة)")}
          </label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+96550000000" dir="ltr" className="text-left" />
          <p className="text-[11px] text-slate-400 mt-1">{tt("تنبيهات الواتساب (إشارة، تحويل محادثة، تأخّر...) بتوصلك على هذا الرقم.")}</p>
        </div>

        <div className="space-y-1">
          <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{tt("التنبيهات اللي تبي تستقبلها على واتساب:")}</div>
          {CATEGORIES.map((c) => (
            <label key={c.key} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
              <span className="text-sm text-slate-600 dark:text-slate-300">{tt(c.label)}</span>
              <input
                type="checkbox"
                checked={waOn[c.key]}
                onChange={(e) => setWaOn((p) => ({ ...p, [c.key]: e.target.checked }))}
                className="h-4 w-4 accent-emerald-600"
              />
            </label>
          ))}
        </div>

        <div className="rounded-lg bg-slate-50 dark:bg-slate-800 px-3 py-2 text-[12px] text-slate-500 flex items-start gap-1.5">
          <SettingsIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>{tt("تقدر تغيّر رقمك وتفضيلات التنبيهات بأي وقت من صفحة")} <span className="font-medium">{tt("الإعدادات")}</span>.</span>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={!phoneValid || saving} className="flex-1">
            {saving ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null} {tt("حفظ وبدء الاستخدام")}
          </Button>
          <Button variant="ghost" onClick={() => setDismissed(true)} disabled={saving} className="text-slate-400">{tt("لاحقاً")}</Button>
        </div>
      </div>
    </div>
  );
}
